import { parseDuration } from "../../utils/parseDuration";

type LogFilterTypes = {
    log_level: FilterTags[],
    log_type: FilterTags[],
    from_date: FilterTags[],
    to_date: FilterTags[],
    search: string
}
type FilterTags = {
    name: string,
    id: string
};

export const logGlobalSearch = (searchSubString: string, logsFilter: any) => {
    return { ...logsFilter, search: searchSubString };
}

export const filterLogType = (e: any, logsFilter: any, t: (key: string) => string) => {
    if (!e) {
        return { ...logsFilter, log_type: [] };
    }
    let tempArray: FilterTags[] = logsFilter.log_type;
    let type = e.value;
    let label = "";
    switch (type) {
        case "cdc":
            label = t("Changes");
            break;
        case "ev":
            label = t("Activity");
            break;
        default:
            label = "--";
            break;
    }

    if (tempArray.map(a => a.id).includes(type)) {
        let removed: FilterTags[] = []
        for (var item in tempArray) {
            if (tempArray[item].id !== type) {
                removed.push(tempArray[item])
            }
        }
        return { ...logsFilter, log_type: removed };
    } else {
        let newItem = {
            name: label,
            id: type,
            attribute_name: "type"
        }
        return { ...logsFilter, log_type: [newItem] };
    }
}

export const filterLogStatus = (e: any, logsFilter: any, t: (key: string) => string) => {
    if (!e) {
        return { ...logsFilter, log_level: [] };
    }
    let tempArray: FilterTags[] = logsFilter.log_level;
    let level = e.value;
    let label = "";
    switch (level) {
        case "10":
            label = t("All logs");
            break;
        case "20":
            label = t("Basic");
            break;
        case "30":
            label = t("Warning");
            break;
        case "40":
            label = t("Error");
            break;
        default:
            label = "--";
            break;
    }

    if (tempArray.map(a => a.id).includes(level)) {
        let removed: FilterTags[] = []
        for (var item in tempArray) {
            if (tempArray[item].id !== level) {
                removed.push(tempArray[item])
            }
        }
        return { ...logsFilter, log_level: removed };
    } else {
        let newItem = {
            name: label,
            id: level,
            attribute_name: "status"
        }
        return { ...logsFilter, log_level: [newItem] };
    }
}

export const filterLogDate = (e: any, side: string, logsFilter: any, t: (key: string) => string) => {
    if (!e) {
        return { ...logsFilter, [side]: [] };
    }

    if (typeof e.value === 'string' && e.value === 'custom') {
        return { ...logsFilter };
    }

    let dateValue: Date;

    if (e.value instanceof Date) {
        dateValue = e.value;
    } else if (typeof e.value === 'string') {
        const parsed = parseDuration(e.value);
        if (parsed) {
            dateValue = parsed;
        } else {
            const daysAgo = parseInt(e.value, 10);
            if (!isNaN(daysAgo)) {
                dateValue = new Date();
                dateValue.setDate(dateValue.getDate() - daysAgo);
            } else {
                return { ...logsFilter };
            }
        }
    } else {
        dateValue = e.value;
    }

    let newItem = {
        name: side === "to_date" ? t("End Date & Time") : t("Start Date & Time"),
        id: dateValue instanceof Date ? dateValue.toISOString() : e.value,
        attribute_name: "datetime"
    }

    return { ...logsFilter, [side]: [newItem] };
}

export const removeLogFilterTag = (e: any, logsFilter: any) => {
    if (logsFilter.log_level.map((a: any) => a.id).includes(e)) {
        let tempArray: FilterTags[] = logsFilter.log_level
        let removed: FilterTags[] = []
        for (var item in tempArray) {
            if (tempArray[item].id !== e) removed.push(tempArray[item])
        }
        return { ...logsFilter, log_level: removed };
    } else if (logsFilter.log_type.map((a: any) => a.id).includes(e)) {
        let tempArray: FilterTags[] = logsFilter.log_type
        let removed: FilterTags[] = []
        for (item in tempArray) {
            if (tempArray[item].id !== e) removed.push(tempArray[item])
        }
        return { ...logsFilter, log_type: removed };
    } else if (logsFilter.from_date.map((a: any) => a.id).includes(e)) {
        let tempArray: FilterTags[] = logsFilter.from_date
        let removed: FilterTags[] = []
        for (item in tempArray) {
            if (tempArray[item].id !== e) removed.push(tempArray[item])
        }
        return { ...logsFilter, from_date: removed };
    } else {
        let tempArray: FilterTags[] = logsFilter.to_date
        let removed: FilterTags[] = []
        for (item in tempArray) {
            if (tempArray[item].id !== e) removed.push(tempArray[item])
        }
        return { ...logsFilter, to_date: removed };
    }
}

export const resetLogFilters = () => {
    return {
        log_level: [],
        log_type: [],
        from_date: [],
        to_date: [],
        search: "",
        ordering: ""
    }
}

export const parseCursor = (url: string): string | null => {
    if (url) {
        const urlObj = new URL(url);
        return urlObj.searchParams.get("cursor");
    } else {
        return null
    }
};

// Largest-first unit ladder for relative time. Months/years are calendar
// approximations, which is fine for a "how long ago" hint next to an exact
// timestamp.
const RELATIVE_TIME_UNITS: { unit: Intl.RelativeTimeFormatUnit; ms: number }[] = [
    { unit: "year", ms: 365 * 24 * 60 * 60 * 1000 },
    { unit: "month", ms: 30 * 24 * 60 * 60 * 1000 },
    { unit: "day", ms: 24 * 60 * 60 * 1000 },
    { unit: "hour", ms: 60 * 60 * 1000 },
    { unit: "minute", ms: 60 * 1000 },
    { unit: "second", ms: 1000 },
];

// Human-friendly age of a raw UTC datetime ("5 minutes ago", "hace 5 minutos").
// Deliberately computed from the raw created_at value, not the timezone-adjusted
// display string — relative time is timezone-independent. Future timestamps
// (client/server clock skew) clamp to "now". `now` is injectable for tests only.
export const formatRelativeTime = (isoDatetime: string, locale: string, now: Date = new Date()): string => {
    const parsedMs = new Date(isoDatetime).getTime();
    // An unparseable isoDatetime yields NaN here; without this guard the NaN
    // would propagate through elapsedMs and fall through to "now", which
    // misleadingly implies a valid, recent timestamp.
    if (Number.isNaN(parsedMs)) {
        return "";
    }
    const elapsedMs = Math.max(0, now.getTime() - parsedMs);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    for (const { unit, ms } of RELATIVE_TIME_UNITS) {
        if (elapsedMs >= ms) {
            return rtf.format(-Math.floor(elapsedMs / ms), unit);
        }
    }
    return rtf.format(0, "second");
};
